import React, { useEffect, useLayoutEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import { toast } from 'react-toastify';
import { useNavigate } from 'react-router-dom';
import { History } from 'lucide-react';
import { setTopNavNameTitle } from '../../redux/features/mobileLayout/slice';
import { useAppDispatch } from '../../redux/store';
import { AvailableShiftCard, RequestCard, DateSelectionModal, ClaimConfirmationModal } from '../../components/OpenShifts';
import { OpenShift } from '../../types/model/openShift';
import { getOpenShifts, claimOpenShifts, getMyOpenShiftRequests, cancelOpenShiftRequest } from '../../redux/features/openShift';
import { GetOpenShiftsRequest, ClaimOpenShiftRequest } from '../../types/request';
import { RootState } from '../../redux/store';
import DateTimeUtil from '../../utils/dateTime';
import { formatTimeCardDate } from '../../utils/employeeTimeCardhelper';

const OpenShiftsMaster: React.FC = () => {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const [selectedShiftIds, setSelectedShiftIds] = useState<string[]>([]);
  const [isDateModalOpen, setIsDateModalOpen] = useState(false);
  const [isClaimModalOpen, setIsClaimModalOpen] = useState(false);
  const [cancelingShiftId, setCancelingShiftId] = useState<string | null>(null);
  
  // Get 7 days from today as default date range
  const now = new Date();
  const startDate7Days = new Date(now);
  const endDate7Days = new Date(now);
  endDate7Days.setDate(now.getDate() + 7);
  
  const [startDate, setStartDate] = useState(startDate7Days.toISOString().split('T')[0]);
  const [endDate, setEndDate] = useState(endDate7Days.toISOString().split('T')[0]);

  // Get open shifts and my requests from Redux store
  const { openShifts, isLoadingOpenShifts, isCreatingOpenShift, error } = useSelector((state: RootState) => state.openShift);

  // Helper function to check if an error message indicates "no data found" scenario
  const isNoDataFoundError = (errorMessage: string | null | undefined): boolean => {
    if (!errorMessage) return false;
    const noDataPatterns = [
      'no open shift',
      'no data found',
      'no shifts found',
      'not found',
      '404',
    ];
    const lowerError = errorMessage.toLowerCase();
    return noDataPatterns.some(pattern => lowerError.includes(pattern));
  };

  useLayoutEffect(() => {
    dispatch(setTopNavNameTitle("Open Shifts"));
  }, []);

  // Helper function to format date as DD-MM-YYYY for API
  const formatDateForAPI = (dateString: string) => {
    // Ensure the date is treated as UTC to avoid timezone issues
    // If the date string doesn't have time info, append T00:00:00Z to make it UTC
    let dateStringUTC = dateString;
    if (!dateString.includes('T')) {
      dateStringUTC = dateString + 'T00:00:00Z';
    } else if (!dateString.endsWith('Z') && !dateString.includes('+')) {
      // If it has time but no timezone, assume it's UTC
      dateStringUTC = dateString + 'Z';
    }
    
    const date = new Date(dateStringUTC);
    const day = date.getUTCDate().toString().padStart(2, '0');
    const month = (date.getUTCMonth() + 1).toString().padStart(2, '0');
    const year = date.getUTCFullYear();
    return `${day}-${month}-${year}`;
  };

  // Helper function to calculate days difference
  const getDaysDifference = (start: string, end: string) => {
    const startDate = new Date(start);
    const endDate = new Date(end);
    const diffTime = Math.abs(endDate.getTime() - startDate.getTime());
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24)) + 1; // +1 to include both start and end dates
    return diffDays;
  };

  // Helper function to format date range display
  const formatDateRangeDisplay = (start: string, end: string) => {
    const days = getDaysDifference(start, end);
    return `Showing ${days} day${days > 1 ? 's' : ''}`;
  };

  useEffect(() => {
    // Fetch open shifts when component mounts with date range
    const dateRange: GetOpenShiftsRequest = {
      start_date: formatDateForAPI(startDate),
      end_date: formatDateForAPI(endDate)
    };
    dispatch(getOpenShifts(dateRange));
  }, [dispatch, startDate, endDate]);

  useEffect(() => {
    // Fetch my open shift requests when component mounts
    dispatch(getMyOpenShiftRequests());
  }, [dispatch]);

  // State for my requests (temporary until Redux state is updated)
  const [myRequests, setMyRequests] = useState<OpenShift[]>([]);
  const [isLoadingMyRequests, setIsLoadingMyRequests] = useState(false);
  const [errorMyRequests, setErrorMyRequests] = useState<string | null>(null);

  // Fetch my requests manually for now
  useEffect(() => {
    const fetchMyRequests = async () => {
      setIsLoadingMyRequests(true);
      setErrorMyRequests(null);
      try {
        const result = await dispatch(getMyOpenShiftRequests()).unwrap();
        setMyRequests(result);
      } catch (error: any) {
        // When using rejectWithValue, the error is the payload itself (a string)
        const errorMessage = typeof error === 'string' ? error : (error.message || "Failed to load my requests");
        setErrorMyRequests(errorMessage);
      } finally {
        setIsLoadingMyRequests(false);
      }
    };

    fetchMyRequests();
  }, [dispatch]);

  const handleShiftSelect = (id: string) => {
    setSelectedShiftIds(prev => {
      if (prev.includes(id)) {
        return prev.filter(shiftId => shiftId !== id);
      } else { 
        return [...prev, id];
      }
    });
  };

  const handleClaimButtonClick = () => {
    if (selectedShiftIds.length === 0) {
      toast.error("Please select at least one shift to claim.");
      return;
    }
    setIsClaimModalOpen(true);
  };

  const handleClaimConfirm = async (notes: string) => {
    try {
      const claimData: ClaimOpenShiftRequest = {
        shift_ids: selectedShiftIds,
        notes: notes.trim() || undefined
      };
      
      await dispatch(claimOpenShifts(claimData)).unwrap().then((response) => {
        if(response) {
          toast.success(response);
        } else {
          toast.success(`Successfully claimed ${selectedShiftIds.length} shift${selectedShiftIds.length > 1 ? 's' : ''}!`);
        }
      }).catch((error) => {
        toast.error(error || "Failed to claim shifts. Please try again.");
      });
      
      // Clear selections and close modal after successful claim
      setSelectedShiftIds([]);
      setIsClaimModalOpen(false);
      
      // Refresh both open shifts and my requests data
      const dateRange: GetOpenShiftsRequest = {
        start_date: formatDateForAPI(startDate),
        end_date: formatDateForAPI(endDate)
      };
      dispatch(getOpenShifts(dateRange));
      
      // Refresh my requests
      const fetchMyRequests = async () => {
        try {
          const result = await dispatch(getMyOpenShiftRequests()).unwrap();
          setMyRequests(result);
        } catch (error: any) {
          console.error('Failed to refresh my requests:', error);
        }
      };
      fetchMyRequests();
      
    } catch (error: any) {
      toast.error(error.message || "Failed to claim shifts. Please try again.");
    }
  };

  const handleClaimCancel = () => {
    // Reset selections and close modal
    setSelectedShiftIds([]);
    setIsClaimModalOpen(false);
  };

  const handleCancelShiftRequest = async (shiftId: string) => {
    try {
      setCancelingShiftId(shiftId);
      await dispatch(cancelOpenShiftRequest(shiftId)).unwrap();
      toast.success("Shift request cancelled successfully!");
      
      // Refresh my requests data
      const fetchMyRequests = async () => {
        try {
          const result = await dispatch(getMyOpenShiftRequests()).unwrap();
          setMyRequests(result);
        } catch (error: any) {
          console.error('Failed to refresh my requests:', error);
        }
      };
      fetchMyRequests();
      
    } catch (error: any) {
      toast.error(error.message || "Failed to cancel shift request. Please try again.");
    } finally {
      setCancelingShiftId(null);
    }
  };

  const handleDateChange = (newStartDate: string, newEndDate: string) => {
    setStartDate(newStartDate);
    setEndDate(newEndDate);
    // The useEffect will automatically fetch new data based on the date range
  };

  // Helper function to format date using DateTimeUtil
  const formatDate = (date?: string) => {
    if (!date) return "No date";
    // return DateTimeUtil.formatFullDate(new Date(date));
    return formatTimeCardDate(date);
  };

  // Helper function to format time using DateTimeUtil
  const formatTime = (startTime?: string, endTime?: string) => {
    if (!startTime || !endTime) return "No time specified";
    
    // Convert time strings like "09:00:00" to readable format
    const formatTimeString = (timeStr: string) => {
      try {
        // Parse time string and format it
        const [hours, minutes] = timeStr.split(':');
        const hour = parseInt(hours, 10);
        const ampm = hour >= 12 ? 'PM' : 'AM';
        const displayHour = hour === 0 ? 12 : hour > 12 ? hour - 12 : hour;
        return `${displayHour}:${minutes} ${ampm}`;
      } catch {
        return timeStr; // Fallback to original string if parsing fails
      }
    };
    
    return `${formatTimeString(startTime)} – ${formatTimeString(endTime)}`;
  };

  // Helper function to get status from OpenShift data
  const getStatusFromOpenShift = (shift: OpenShift): 'pending' | 'approved' | 'cancelled' => {
    // Map the status from the API response to our UI status
    const apiStatus = shift.status?.toLowerCase();
    if (apiStatus === 'approved') return 'approved';
    if (apiStatus === 'cancelled') return 'cancelled';
    return 'pending'; // Default to pending for any other status
  };

  return (
    <div className="min-h-screen bg-white px-5 pt-6 pb-10 max-w-screen-sm mx-auto">
      {/* Available Shifts */}
      <section className="mt-1">
        <div className="flex items-center justify-between">
          <h2 className="text-[22px] leading-[28px] font-normal text-gray-800">Available Shifts</h2>
          <div className="flex items-center space-x-3">
            {/* History Icon Button */}
            <button
              onClick={() => navigate('/open-shifts-history')}
              className="p-2 text-gray-600 hover:text-blue-600 transition-colors"
              title="View Shift History"
            >
              <History className="w-6 h-6" />
            </button>
            
            {/* Claim Button */}
            {openShifts.length > 0 && selectedShiftIds.length > 0 && (
              <button
                onClick={handleClaimButtonClick}
                disabled={isCreatingOpenShift}
                className={`h-[40px] px-4 rounded-xl text-[15px] font-normal shadow-sm active:translate-y-[0.5px] transition-colors border-2 ${
                  isCreatingOpenShift 
                    ? 'bg-gray-50 border-gray-300 text-gray-400 cursor-not-allowed' 
                    : 'bg-white border-blue-600 text-blue-600 hover:bg-blue-50'
                }`}
              >
                Claim ({selectedShiftIds.length})
              </button>
            )}
          </div>
        </div>
        
        <div className="mt-2 flex items-center text-[16px] leading-6 text-gray-500">
          <span>{formatDateRangeDisplay(startDate, endDate)}</span>
          <span className="mx-2">—</span>
          <button 
            type="button" 
            className="text-blue-600 font-normal active:opacity-90 hover:text-blue-700"
            onClick={() => setIsDateModalOpen(true)}
          >
            Change dates
          </button>
        </div>

        {/* Loading State */}
        {isLoadingOpenShifts && (
          <div className="flex justify-center items-center py-8">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-500"></div>
          </div>
        )}

        {/* Error State - only show for actual errors, not "no data found" scenarios */}
        {error && !isNoDataFoundError(error) && (
          <div className="bg-red-50 border border-red-200 rounded-lg p-4 mb-4">
            <p className="text-red-600">Error loading open shifts: {error}</p>
          </div>
        )}

        {/* Available Shift Cards - show empty state for both no data and "no data found" errors */}
        {!isLoadingOpenShifts && (!error || isNoDataFoundError(error)) && (
          <>
            {openShifts.length === 0 ? (
              <div className="text-center py-8 text-gray-500">
                No open shifts available for the selected date range.
              </div>
            ) : (
              openShifts.map((shift) => (
                <AvailableShiftCard
                  key={shift.os_sch_open_shift_transactions}
                  id={shift.os_sch_open_shift_transactions || ''}
                  location={shift.shift_details?.shift_name || 'Unnamed Shift'}
                  date={formatDate(shift.shift_date)}
                  time={formatTime(shift.shift_details?.shift_start_time, shift.shift_details?.shift_end_time)}
                  // note={`Code: ${shift.shift_details?.shift_code || 'N/A'}`}
                  isSelected={selectedShiftIds.includes(shift.os_sch_open_shift_transactions || '')}
                  onSelect={handleShiftSelect}
                />
              ))
            )}
          </>
        )}
      </section>

      {/* My Requests */}
      <section className="mt-8">
        <h2 className="text-[22px] leading-[28px] font-normal text-gray-800">My Requests</h2>

        {/* Loading State for My Requests */}
        {isLoadingMyRequests && (
          <div className="flex justify-center items-center py-8">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-500"></div>
          </div>
        )}

        {/* Error State for My Requests - only show for actual errors, not "no data found" scenarios */}
        {errorMyRequests && !isNoDataFoundError(errorMyRequests) && (
          <div className="bg-red-50 border border-red-200 rounded-lg p-4 mb-4">
            <p className="text-red-600">Error loading my requests: {errorMyRequests}</p>
          </div>
        )}

        {/* Request Cards - show empty state for both no data and "no data found" errors */}
        {!isLoadingMyRequests && (!errorMyRequests || isNoDataFoundError(errorMyRequests)) && (
          <>
            {myRequests && myRequests.length > 0 ? (
              myRequests.map((shift) => (
                <RequestCard
                  key={shift.os_sch_open_shift_transactions}
                  id={shift.os_sch_open_shift_transactions || ''}
                  location={shift.shift_details?.shift_name || 'Unnamed Shift'}
                  date={formatDate(shift.shift_date)}
                  time={shift.shift_details?.shift_start_time && shift.shift_details?.shift_end_time ? 
                    formatTime(shift.shift_details.shift_start_time, shift.shift_details.shift_end_time) : 
                    undefined}
                  status={getStatusFromOpenShift(shift)}
                  onCancel={handleCancelShiftRequest}
                  isCanceling={cancelingShiftId === (shift.os_sch_open_shift_transactions || '')}
                />
              ))
            ) : (
              <div className="text-center py-8 text-gray-500">
                No shift requests found.
              </div>
            )}
          </>
        )}
      </section>

      {/* Date Selection Modal */}
      <DateSelectionModal
        isOpen={isDateModalOpen}
        onClose={() => setIsDateModalOpen(false)}
        onDateChange={handleDateChange}
        currentStartDate={startDate}
        currentEndDate={endDate}
      />

      {/* Claim Confirmation Modal */}
      <ClaimConfirmationModal
        isOpen={isClaimModalOpen}
        onClose={handleClaimCancel}
        onConfirm={handleClaimConfirm}
        selectedCount={selectedShiftIds.length}
        isLoading={isCreatingOpenShift}
      />
    </div>
  );
};

export default OpenShiftsMaster;
